import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  complaintApi,
  ComplaintFilters,
  CreateComplaintInput,
  UpdateComplaintInput,
} from "@/api/complaint.api";
import { ComplaintStatus } from "@/components/shared/StatusBadge";

const LIST_KEY = "complaints";

export function useComplaints(filters: ComplaintFilters) {
  return useQuery({
    queryKey: [LIST_KEY, filters],
    queryFn: () => complaintApi.list(filters),
    placeholderData: (prev) => prev,
  });
}

export function useComplaint(id: string | undefined) {
  return useQuery({
    queryKey: [LIST_KEY, id],
    queryFn: () => complaintApi.getById(id as string),
    enabled: Boolean(id),
  });
}

function useInvalidateComplaint(id: string) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [LIST_KEY] });
    queryClient.invalidateQueries({ queryKey: [LIST_KEY, id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  };
}

export function useCreateComplaint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateComplaintInput) => complaintApi.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LIST_KEY] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useUpdateComplaint(id: string) {
  const invalidate = useInvalidateComplaint(id);
  return useMutation({
    mutationFn: (input: UpdateComplaintInput) => complaintApi.update(id, input),
    onSuccess: invalidate,
  });
}

export function useChangeStatus(id: string) {
  const invalidate = useInvalidateComplaint(id);
  return useMutation({
    mutationFn: ({ status, remarks }: { status: ComplaintStatus; remarks?: string }) =>
      complaintApi.changeStatus(id, status, remarks),
    onSuccess: invalidate,
  });
}

export function useAssignEmployee(id: string) {
  const invalidate = useInvalidateComplaint(id);
  return useMutation({
    mutationFn: ({ employeeId, remarks }: { employeeId: string; remarks?: string }) =>
      complaintApi.assign(id, employeeId, remarks),
    onSuccess: invalidate,
  });
}

export function useAddNote(id: string) {
  const invalidate = useInvalidateComplaint(id);
  return useMutation({
    mutationFn: (remarks: string) => complaintApi.addNote(id, remarks),
    onSuccess: invalidate,
  });
}

export function useLogHours(id: string) {
  const invalidate = useInvalidateComplaint(id);
  return useMutation({
    mutationFn: ({ workingHours, remarks }: { workingHours: number; remarks?: string }) =>
      complaintApi.logHours(id, workingHours, remarks),
    onSuccess: invalidate,
  });
}

export function useUploadAttachment(id: string) {
  const invalidate = useInvalidateComplaint(id);
  return useMutation({
    mutationFn: (file: File) => complaintApi.uploadAttachment(id, file),
    onSuccess: invalidate,
  });
}

export function useRemoveAttachment(id: string) {
  const invalidate = useInvalidateComplaint(id);
  return useMutation({
    mutationFn: (attachmentId: string) => complaintApi.removeAttachment(id, attachmentId),
    onSuccess: invalidate,
  });
}

export function useDeleteComplaint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => complaintApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LIST_KEY] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}
